import {
  Banner,
  Box,
  FieldLabel,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { ampli } from 'lib-shared-tracking';
import type { OpeningHours, Schedule } from 'lib-shared-types-api';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { v4 as uuid } from 'uuid';

import { flowsApi } from 'apis/flows';
import { templatesApi } from 'apis/templates';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import {
  FlowManagerEditModalComponentT,
  FlowType,
  NodeT,
} from 'fable/flowEditor';
import { ToggleBox, ToggleBoxHead } from 'fable/toggleBox';
import { trackFlowActionSaved } from 'utils/flowEditor/tracking';

import { BusinessHoursPayload } from '../../nodeTypes/canvas/businesshours';
import { FlowNodeTypes } from '../../types';

import { AutoReplies } from './components/autoReplies';
import { BusinessHours } from './components/businessHours/businessHours';
import { Item } from './components/listItem/listItem';
import { ScheduleSelect } from './components/scheduleSelect/scheduleSelect';

const scheduleField = 'schedule';
const finalStepOfMigration = 5;

export type BusinessHoursNode = NodeT<
  FlowNodeTypes.BUSINESS_HOURS,
  BusinessHoursPayload
>;
export type Props = FlowManagerEditModalComponentT<BusinessHoursNode>;

type Errors = {
  [Key in typeof scheduleField]?: string;
};

function getPhoneAudioMessage({
  name,
  recurring,
  announcement,
}: OpeningHours): Item {
  return {
    name,
    isRecurring: !!recurring,
    value: {
      announcement: announcement?.url || '',
      label: announcement?.label || 'No auto-reply set',
    },
  };
}

function getTemplateAutoReply({
  name,
  recurring,
  auto_reply_template_id,
}: OpeningHours): Item | null {
  return auto_reply_template_id
    ? {
        name,
        isRecurring: !!recurring,
        value: {
          label: auto_reply_template_id || 'No auto-reply set',
        },
      }
    : null;
}

export function createBusinessHoursModal(
  flowType: FlowType,
): FunctionComponent<Props> {
  return function BusinessHoursModal({
    node,
    nodeLabel,
    replaceNode,
    triggerClose,
  }: Props): JSX.Element {
    const { isRemovable, payload } = node;

    // `isRemovable` will be set to false if node has just been migrated.
    // The moment it's "Saved" it needs to be changed to true.
    const isMigrated = !isRemovable;

    const isPhoneNode = flowType === FlowType.FLOW_TYPE_CALL;
    const savedHangUpAfterAnnouncement =
      payload?.hangUpAfterAnnouncement ?? false;
    const savedScheduleId = payload?.scheduleId ?? '';
    const [hangUpAfterAnnouncement, setHangUpAfterAnnouncement] = useState(
      savedHangUpAfterAnnouncement,
    );
    const [scheduleId, setScheduleId] =
      useState<Schedule['id']>(savedScheduleId);
    const [
      readFlowSchedule,
      { data: schedule, isFetching: isScheduleLoading },
    ] = flowsApi.useLazyReadFlowScheduleQuery();
    const [errors, setErrors] = useState<Errors>({});

    const isChanged = useIsChanged(
      [savedHangUpAfterAnnouncement, hangUpAfterAnnouncement],
      [savedScheduleId, scheduleId],
    );

    const openingHours = schedule?.opening_hours;

    const { template } = templatesApi.useReadTemplateByIdQuery(
      schedule?.auto_reply_template_id
        ? { id: schedule.auto_reply_template_id }
        : skipToken,
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          template: data?.data,
        }),
      },
    );

    const defaultAudioMessageOrAutoReply = useMemo((): null | Item => {
      if (!schedule) return null;
      const { announcement } = schedule;
      return isPhoneNode
        ? {
            name: 'Standard audio message',
            isRecurring: false,
            value: {
              announcement: announcement.url || '',
              label: announcement?.label || 'No auto-reply set',
            },
          }
        : {
            name: 'Standard auto-reply',
            isRecurring: false,
            value: {
              label: template?.name ?? 'No standard auto-reply set',
            },
          };
    }, [isPhoneNode, schedule, template]);

    const listItems = useMemo(
      () =>
        openingHours?.reduce<{
          default: null | Item;
          exceptions: Item[];
        }>(
          (acc, openingHours) => {
            const { announcement, auto_reply_template_id } = openingHours;
            const reply = isPhoneNode
              ? getPhoneAudioMessage(openingHours)
              : getTemplateAutoReply(openingHours);

            if (
              (isPhoneNode ? announcement : auto_reply_template_id) &&
              reply
            ) {
              acc.exceptions.push(reply);
            }

            return acc;
          },
          {
            default: defaultAudioMessageOrAutoReply,
            exceptions: [],
          },
        ),
      [
        isPhoneNode,
        openingHours,
        schedule,
        defaultAudioMessageOrAutoReply,
        template,
      ],
    );

    const validate = useCallback((): boolean => {
      const errors: Errors = {};

      if (!scheduleId) {
        errors[scheduleField] = 'Please select a schedule';
      }

      setErrors(errors);

      return Object.keys(errors).length === 0;
    }, [scheduleId]);

    const handleSaveClick = useCallback(() => {
      const isValid = validate();

      if (!isValid) {
        return;
      }

      const { payload } = node;

      if (isMigrated) {
        ampli.businessHoursMigrationWizardFormSubmitted({
          step: finalStepOfMigration,
          hangUpAfterAnnouncement,
        });
      } else {
        trackFlowActionSaved(!!node.isAction, {
          flowType,
          nodeType: FlowNodeTypes.BUSINESS_HOURS,
          settings: {
            ...(payload?.scheduleId !== scheduleId && { scheduleId }),
            ...(payload?.hangUpAfterAnnouncement !==
              hangUpAfterAnnouncement && {
              hangUpAfterAnnouncement,
            }),
          },
        });
      }

      const newNode: BusinessHoursNode = {
        ...node,
        nodeType: FlowNodeTypes.BUSINESS_HOURS,
        text: 'Business hours',
        icon: 'clock',
        isRemovable: true,
        isEditable: true,
        isAction: false,
        overlay: [],
        badges: [],
        payload: {
          ...payload,
          scheduleId,
          hangUpAfterAnnouncement,
          closedNodeId: hangUpAfterAnnouncement
            ? uuid()
            : payload?.closedNodeId || uuid(),
          openNodeId: payload?.openNodeId || uuid(),
        },
      };

      triggerClose();
      replaceNode(newNode);
    }, [
      validate,
      scheduleId,
      hangUpAfterAnnouncement,
      isMigrated,
      node,
      replaceNode,
      triggerClose,
    ]);

    const handleScheduleChange = useCallback(
      (id) => {
        const scheduleId = id ?? '';

        setScheduleId(scheduleId);
        readFlowSchedule({ id: scheduleId });
      },
      [readFlowSchedule],
    );

    useEffectOnce(() => {
      if (scheduleId) {
        readFlowSchedule({ id: scheduleId });
      }
    });

    const hasDefaultTemplate = !!schedule?.auto_reply_template_id;
    const hasAnyTemplates = hasDefaultTemplate || listItems?.exceptions.length;

    return (
      <Box width="600px">
        <ModalHead
          {...(!isMigrated && { onClose: triggerClose })}
          title={nodeLabel}
        >
          {isMigrated ? (
            <Banner
              icon="route"
              variant="announcement"
              description={
                <Text.Paragraph variant="4">
                  Congrats! You can now use this schedule across as many flows
                  as you like. You can always go here to edit your schedules:
                  Settings &gt; Manage &gt; Business hours
                </Text.Paragraph>
              }
            />
          ) : (
            <Text.Paragraph subdued>
              Decide what happens to incoming conversations based on whether
              your business is open or closed. You can create schedules by going
              to Settings &gt; Manage &gt; Business hours &gt; Add new schedule.
            </Text.Paragraph>
          )}
        </ModalHead>
        <form onSubmit={handleSaveClick}>
          <ModalBody>
            <FormGrid>
              <FormField>
                <ScheduleSelect
                  onChange={handleScheduleChange}
                  error={errors[scheduleField]}
                  value={scheduleId}
                />
              </FormField>
              {!!scheduleId && (
                <>
                  <FormField>
                    <FieldLabel>Business hours</FieldLabel>
                    <BusinessHours
                      isScheduleSelected={!!scheduleId}
                      openingHours={openingHours}
                    />
                  </FormField>
                  {flowType !== FlowType.FLOW_TYPE_MESSENGER && (
                    <AutoReplies
                      isPhoneNode={isPhoneNode}
                      listItems={listItems}
                      isScheduleLoading={isScheduleLoading}
                      hasAnyTemplates={!!hasAnyTemplates}
                      hasDefaultTemplate={hasDefaultTemplate}
                    />
                  )}
                  {isPhoneNode && (
                    <FormField>
                      <ToggleBox
                        isOpen={hangUpAfterAnnouncement}
                        onChange={setHangUpAfterAnnouncement}
                        headComponent={ToggleBoxHead}
                        title="Hang up after audio message"
                        input={{
                          id: 'hang-up-afer-announcement',
                          name: 'Hang Up After Announcement',
                          value: 'hang-up-afer-announcement',
                        }}
                      />
                    </FormField>
                  )}
                </>
              )}
            </FormGrid>
          </ModalBody>
          <ModalFoot
            {...(!isMigrated && { cancelOnClick: triggerClose })}
            confirmName={isMigrated ? 'Finish' : getConfirmButtonVerb(node)}
            confirmProps={{ disabled: !isChanged && !isMigrated }}
            confirmOnClick={handleSaveClick}
          />
        </form>
      </Box>
    );
  };
}
